import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { db } from "@/lib/firebase";
import { collection, getDocs, deleteDoc, doc, updateDoc, query, where, getDocs as getDocsAlias } from "firebase/firestore";
import { Search, FileText, ExternalLink, Calendar, Building2, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";

interface Enrollment {
  id: string;
  studentId: string;
  courseId: string;
  branch: string;
  courseFee: number;
  amountReceived: number;
  balance: number;
  status: string;
  enrolledAt: any;
  paymentMethod: string;
  paymentReceived: boolean;
  registrationType: string;
  certificateUrl?: string;
  completedAt?: any;
  studentName?: string;
  courseName?: string;
  branchName?: string;
  // Additional fields from the data structure
  certificateUploadedAt?: any;
  certificateUploadedBy?: string;
  completedBy?: string;
  enrolledBy?: string;
  lastPaymentAmount?: number;
  lastPaymentAt?: any;
  lastPaymentMethod?: string;
  lastPaymentNotes?: string;
  licenseCodeIds?: string[];
  licenseCodeTotalPrice?: number;
  licenseCodes?: string[];
  licenseCodesAssignedAt?: any;
  licenseCodesAssignedBy?: string;
  notes?: string;
  orNumber?: string;
  orNotes?: string;
  orNumbers?: string[]; // Array of OR numbers for multiple payments
  orEntries?: Array<{
    orNumber: string;
    notes: string;
    amount: number;
    addedAt: any;
    addedBy: string;
  }>; // Detailed OR entries
  // Discount fields
  discountAmount?: number;
  discountType?: string;
  discountReason?: string;
  discountApplied?: boolean;
}

const AdminPayments: React.FC = () => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [filteredEnrollments, setFilteredEnrollments] = useState<Enrollment[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enrollmentToDelete, setEnrollmentToDelete] = useState<Enrollment | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all required data
        const [enrollmentsSnapshot, studentsSnapshot, coursesSnapshot, branchesSnapshot] = await Promise.all([
          getDocs(collection(db, "enrollments")),
          getDocs(collection(db, "users")),
          getDocs(collection(db, "courses")),
          getDocs(collection(db, "branches"))
        ]);

        const enrollmentsData = enrollmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as any[];
        const studentsData = studentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as any[];
        const coursesData = coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as any[];
        const branchesData = branchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as any[];

        // Create lookup maps
        const studentMap = new Map(studentsData.map(s => [s.id, s]));
        const courseMap = new Map(coursesData.map(c => [c.id, c]));
        const branchMap = new Map(branchesData.map(b => [b.id, b]));

        // Enrich enrollments with student, course, and branch names
        const enrichedEnrollments = enrollmentsData.map((enrollment: any) => {
          const student = studentMap.get(enrollment.studentId);
          const course = courseMap.get(enrollment.courseId);
          const branch = branchMap.get(enrollment.branch);

          return {
            ...enrollment,
            studentName: student ? `${student.firstName} ${student.lastName}` : 'Unknown Student',
            courseName: course ? course.title : 'Unknown Course',
            branchName: branch ? branch.name : 'Unknown Branch'
          } as Enrollment;
        });

        setEnrollments(enrichedEnrollments);
        setFilteredEnrollments(enrichedEnrollments);
        setStudents(studentsData);
        setCourses(coursesData);
        setBranches(branchesData);
      } catch (err: any) {
        setError("Failed to load payments data.");
        console.error("Error fetching payments data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Set initial branch when branches are loaded
  useEffect(() => {
    if (branches.length > 0 && !selectedBranch) {
      setSelectedBranch(branches[0].id);
    }
  }, [branches, selectedBranch]);

  // Update filtered enrollments when filters change
  useEffect(() => {
    let filtered = enrollments;

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(enrollment =>
        enrollment.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enrollment.courseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enrollment.registrationType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enrollment.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Branch filter - always applied since a branch is always selected
    if (selectedBranch) {
      filtered = filtered.filter(enrollment => enrollment.branch === selectedBranch);
    }

    // Month and year filter - always applied since they have default values
    filtered = filtered.filter(enrollment => {
      if (!enrollment.enrolledAt) return false;
      
      const enrollDate = enrollment.enrolledAt.toDate ? enrollment.enrolledAt.toDate() : new Date(enrollment.enrolledAt);
      const enrollMonth = enrollDate.getMonth() + 1; // getMonth() is 0-indexed
      const enrollYear = enrollDate.getFullYear();

      const matchesMonth = enrollMonth === parseInt(selectedMonth);
      const matchesYear = enrollYear === parseInt(selectedYear);

      return matchesMonth && matchesYear;
    });

    setFilteredEnrollments(filtered);
  }, [enrollments, searchTerm, selectedBranch, selectedMonth, selectedYear]);

  // Pagination logic
  const totalPages = Math.ceil(filteredEnrollments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEnrollments = filteredEnrollments.slice(startIndex, endIndex);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBranch, selectedMonth, selectedYear]);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-100 text-green-800">Completed</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'approved':
        return <Badge variant="default" className="bg-blue-100 text-blue-800">Approved</Badge>;
      case 'partial_payment':
        return <Badge variant="outline" className="text-orange-600 border-orange-600">Partial Payment</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRegistrationTypeBadge = (type: string) => {
    return type === 'walk-in' 
      ? <Badge variant="outline" className="text-orange-600 border-orange-600">Walk-in</Badge>
      : <Badge variant="default" className="bg-blue-100 text-blue-800">Online</Badge>;
  };

  const handleViewCertificate = (certificateUrl: string) => {
    window.open(certificateUrl, '_blank');
  };

  const handleDeleteEnrollment = async () => {
    if (!enrollmentToDelete) return;
    
    setDeleting(true);
    try {
      // Handle OR numbers - return them to available pool
      const orNumbersToReturn = [];
      
      // Collect all OR numbers from this enrollment
      if (enrollmentToDelete.orNumber) {
        orNumbersToReturn.push(enrollmentToDelete.orNumber);
      }
      
      // Check if enrollment has multiple OR numbers (from the enhanced system)
      if (enrollmentToDelete.orNumbers && enrollmentToDelete.orNumbers.length > 0) {
        orNumbersToReturn.push(...enrollmentToDelete.orNumbers);
      }
      
      // Return OR numbers to available pool
      if (orNumbersToReturn.length > 0) {
        // Find the OR batches for this branch
        const orBatchesQuery = query(
          collection(db, "or"),
          where("branch", "==", enrollmentToDelete.branch)
        );
        const orBatchesSnapshot = await getDocs(orBatchesQuery);
        
        for (const batchDoc of orBatchesSnapshot.docs) {
          const batchData = batchDoc.data();
          const usedNumbers = batchData.usedNumbers || [];
          const availableNumbers = batchData.availableNumbers || [];
          
          // Check if any OR numbers from this enrollment are in this batch
          const numbersInThisBatch = orNumbersToReturn.filter(orNum => 
            usedNumbers.includes(orNum) || availableNumbers.includes(orNum)
          );
          
          if (numbersInThisBatch.length > 0) {
            // Move numbers from used back to available
            const updatedUsedNumbers = usedNumbers.filter(num => !numbersInThisBatch.includes(num));
            const updatedAvailableNumbers = [...availableNumbers, ...numbersInThisBatch.filter(num => !availableNumbers.includes(num))];
            
            await updateDoc(doc(db, "or", batchDoc.id), {
              usedNumbers: updatedUsedNumbers,
              availableNumbers: updatedAvailableNumbers,
              updatedAt: new Date()
            });
          }
        }
      }
      
      // Delete associated schedules
      const schedulesQuery = query(
        collection(db, "schedules"),
        where("enrollmentId", "==", enrollmentToDelete.id)
      );
      const schedulesSnapshot = await getDocs(schedulesQuery);
      
      // Delete all schedules for this enrollment
      const deleteSchedulePromises = schedulesSnapshot.docs.map(scheduleDoc => 
        deleteDoc(doc(db, "schedules", scheduleDoc.id))
      );
      await Promise.all(deleteSchedulePromises);
      
      // Delete the enrollment
      await deleteDoc(doc(db, "enrollments", enrollmentToDelete.id));
      
      // Remove from local state
      setEnrollments(prev => prev.filter(e => e.id !== enrollmentToDelete.id));
      setFilteredEnrollments(prev => prev.filter(e => e.id !== enrollmentToDelete.id));
      
      setDeleteDialogOpen(false);
      setEnrollmentToDelete(null);
      
      // Show success message with detailed info
      let successMessage = "Enrollment deleted successfully.";
      if (schedulesSnapshot.docs.length > 0) {
        successMessage += ` ${schedulesSnapshot.docs.length} associated schedule(s) were also deleted.`;
      }
      if (orNumbersToReturn.length > 0) {
        successMessage += ` OR numbers (${orNumbersToReturn.join(', ')}) have been returned to the available pool.`;
      }
      
      // Use a temporary success state instead of error state
      setTimeout(() => setError(null), 5000); // Clear message after 5 seconds
      setError(successMessage);
    } catch (error) {
      console.error("Error deleting enrollment:", error);
      setError("Failed to delete enrollment. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (enrollment: Enrollment) => {
    setEnrollmentToDelete(enrollment);
    setDeleteDialogOpen(true);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Payments Management</h2>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search & Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <Input
                id="search"
                placeholder="Search by student name, course, etc."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="branch">Branch</Label>
              <select
                id="branch"
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full border rounded p-2"
              >
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="month">Month</Label>
              <select
                id="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full border rounded p-2"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="year">Year</Label>
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full border rounded p-2"
              >
                {years.map(year => (
                  <option key={year} value={year.toString()}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enrollments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Enrollments ({filteredEnrollments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          ) : error ? (
            <div className="py-8 text-center text-destructive">{error}</div>
          ) : paginatedEnrollments.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">No enrollments found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Course Name</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Student Name</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Registration Type</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Course Price</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Discount</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Amount Paid</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Balance Due</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">OR Number</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Status</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Branch</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Enrolled Date</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedEnrollments.map((enrollment) => {
                    // Calculate the correct balance considering discounts
                    const courseFee = enrollment.courseFee || 0;
                    const discountAmount = enrollment.discountAmount || 0;
                    const amountReceived = enrollment.amountReceived || 0;
                    const correctBalance = Math.max(0, (courseFee - discountAmount) - amountReceived);
                    
                    return (
                      <tr key={enrollment.id} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="font-medium">{enrollment.courseName}</div>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="font-medium">{enrollment.studentName}</div>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          {getRegistrationTypeBadge(enrollment.registrationType)}
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="font-medium text-blue-600">₱{courseFee.toLocaleString()}</div>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="font-medium text-orange-600">
                            {discountAmount > 0 ? `₱${discountAmount.toLocaleString()}` : '₱0'}
                          </div>
                          {enrollment.discountApplied && enrollment.discountReason && (
                            <div className="text-xs text-gray-500 mt-1">
                              {enrollment.discountReason}
                            </div>
                          )}
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="font-medium text-green-600">₱{amountReceived.toLocaleString()}</div>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className={`font-medium ${correctBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ₱{correctBalance.toLocaleString()}
                          </div>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          {(() => {
                            const allORNumbers = [];
                            
                            // Add legacy OR number if exists
                            if (enrollment.orNumber) {
                              allORNumbers.push(enrollment.orNumber);
                            }
                            
                            // Add new OR numbers if exists
                            if (enrollment.orNumbers && enrollment.orNumbers.length > 0) {
                              allORNumbers.push(...enrollment.orNumbers);
                            }
                            
                            if (allORNumbers.length === 0) {
                              return <span className="text-gray-400 text-sm">—</span>;
                            }
                            
                            return (
                              <div className="flex flex-wrap gap-1">
                                {allORNumbers.map((orNum, index) => (
                                  <div key={index} className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                    {orNum}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          {getStatusBadge(enrollment.status)}
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3 text-gray-400" />
                            <span className="text-sm">{enrollment.branchName}</span>
                          </div>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            <span className="text-sm">
                              {enrollment.enrolledAt 
                                ? format(enrollment.enrolledAt.toDate ? enrollment.enrolledAt.toDate() : new Date(enrollment.enrolledAt), 'MMM dd, yyyy')
                                : 'Unknown'
                              }
                            </span>
                          </div>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="flex items-center gap-2">
                            {enrollment.certificateUrl ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewCertificate(enrollment.certificateUrl!)}
                                className="flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                View Certificate
                              </Button>
                            ) : (
                              <span className="text-sm text-gray-400">No Certificate</span>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteDialog(enrollment)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {filteredEnrollments.length > 0 && totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} - {Math.min(endIndex, filteredEnrollments.length)} of {filteredEnrollments.length} enrollments
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPrevPage}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => goToPage(page)}
                          className="min-w-[40px]"
                        >
                          {page}
                        </Button>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Enrollment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to delete this enrollment? This action cannot be undone.
            </p>
            {enrollmentToDelete && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <div><strong>Student:</strong> {enrollmentToDelete.studentName}</div>
                  <div><strong>Course:</strong> {enrollmentToDelete.courseName}</div>
                  <div><strong>Branch:</strong> {enrollmentToDelete.branchName}</div>
                  <div><strong>Amount Paid:</strong> ₱{enrollmentToDelete.amountReceived?.toLocaleString() || '0'}</div>
                  <div><strong>Status:</strong> {enrollmentToDelete.status}</div>
                  {/* Show OR numbers that will be returned */}
                  {(() => {
                    const orNumbersToReturn = [];
                    if (enrollmentToDelete.orNumber) {
                      orNumbersToReturn.push(enrollmentToDelete.orNumber);
                    }
                    if (enrollmentToDelete.orNumbers && enrollmentToDelete.orNumbers.length > 0) {
                      orNumbersToReturn.push(...enrollmentToDelete.orNumbers);
                    }
                    
                    if (orNumbersToReturn.length > 0) {
                      return (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                          <div className="text-sm font-medium text-yellow-800 mb-1">
                            ⚠️ OR Numbers to be returned:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {orNumbersToReturn.map((orNum, index) => (
                              <span key={index} className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                {orNum}
                              </span>
                            ))}
                          </div>
                          <div className="text-xs text-yellow-700 mt-1">
                            These OR numbers will be returned to the available pool.
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteEnrollment}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete Enrollment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayments;
